import React from 'react';
import { format, parseISO } from 'date-fns';

import { AlertRow } from '../../../../entities/Alert';
import { HealthParameterThreshold } from '../../../../entities/HealthParameter';
import getAlertLabels from '../../../../utils/getAlertLabels';
import getAppointmentOutcomeLabel from '../../../../utils/getAppointmentOutcome';
import AlertStateEnum from '../../../../enums/AlertStateEnum';
import { rulerFor } from '../../../../utils/describeThreshold';

import WhatsappAction from '../WhatsappAction';
import { CANNOT_TREAT_MESSAGE } from '../TreatModal';

import {
  Actions,
  Badge,
  BodyRow,
  Conditions,
  HeaderRow,
  MoreConditions,
  Muted,
  Outcome,
  ParameterCell,
  ParameterLabel,
  PatientLink,
  Ruler,
  Table as Container,
  TreatButton,
  ViewButton,
} from '../styles';

const VISIBLE_CONDITIONS = 2;

interface AlertsTableProps {
  alerts: AlertRow[];
  /** A régua, para a linha "por que disparou" de cada alerta. */
  thresholds: HealthParameterThreshold[];
  onOpenPatient: (alert: AlertRow) => void;
  onView: (alert: AlertRow) => void;
  onTreat: (alert: AlertRow) => void;
  /**
   * Se quem está logado pode abrir atendimento (tem cadastro de profissional). Sem
   * isso o "Tratar" aparece desligado, com o motivo — e não uma janela que se
   * preenche inteira para recusar no fim (achado 9 do QA de 18/09).
   */
  canTreat: boolean;
}

/**
 * A tabela "Histórico de Alertas".
 *
 * 🔒 **Não existe coluna de nome, e não é omissão de layout.** A regra 3 de
 * `02-alertas.md`: a tela é projetável em telão, então a fila identifica o paciente
 * **só por ID**. O nome está a um clique de distância, no prontuário, que é outra
 * tela e outro gate.
 *
 * ⓘ **Três ações por linha, e cada uma resolve uma pergunta diferente:** WhatsApp
 * é falar com o paciente agora, "Tratar" é assumir o caso (abre o atendimento de
 * onde sairá o desfecho) e "Visualizar" é entender o alerta — o informativo com a
 * régua e o histórico de leituras. O prontuário fica no ID do paciente.
 */
const AlertsTable: React.FC<AlertsTableProps> = ({
  alerts,
  thresholds,
  onOpenPatient,
  onView,
  onTreat,
  canTreat,
}) => (
  <Container>
    <HeaderRow>
      <span>Gravidade</span>
      <span>ID</span>
      <span>Idade</span>
      <span>Categoria</span>
      <span>Condições</span>
      <span>Parâmetro</span>
      <span>Score</span>
      <span>Data/hora</span>
      <span>Desfecho</span>
      <span>Ação</span>
    </HeaderRow>

    {alerts.map((alert) => {
      const ruler = rulerFor(thresholds, alert.parameter, alert.severity);

      return (
        <BodyRow key={alert.id}>
          <span>
            <Badge color={getAlertLabels.severityColor(alert.severity)}>
              {getAlertLabels.severity(alert.severity)}
            </Badge>
          </span>

          {/* O ID leva ao prontuário — é o caminho da spec para chegar ao nome. */}
          <PatientLink
            type="button"
            onClick={() => onOpenPatient(alert)}
            title="Abrir prontuário"
          >
            {alert.patientId.slice(0, 8)}
          </PatientLink>

          <span>
            {alert.age !== null ? `${alert.age} anos` : <Muted>—</Muted>}
          </span>

          <span>
            <Badge color={getAlertLabels.categoryColor(alert.category)}>
              {getAlertLabels.category(alert.category)}
            </Badge>
          </span>

          {/*
            Uma condição por linha, no máximo duas à vista: juntas numa linha só com
            reticências, quem tem cinco condições aparecia igual a quem tem uma.
          */}
          <Conditions title={alert.conditions.join('\n')}>
            {alert.conditions.length === 0 && <span>—</span>}
            {alert.conditions.slice(0, VISIBLE_CONDITIONS).map((condition) => (
              <span key={condition}>{condition}</span>
            ))}
            {alert.conditions.length > VISIBLE_CONDITIONS && (
              <MoreConditions>
                +{alert.conditions.length - VISIBLE_CONDITIONS}
              </MoreConditions>
            )}
          </Conditions>

          {/*
            O que disparou o alerta: o parâmetro (rótulo), a leitura e, embaixo, o
            trecho da régua que ela cruzou.
          */}
          <ParameterCell>
            <ParameterLabel>
              {getAlertLabels.parameter(alert.parameter)}
            </ParameterLabel>
            <span>{alert.reading || '—'}</span>
            {ruler && (
              <Ruler
                title={`Régua ${getAlertLabels
                  .severity(alert.severity)
                  .toLowerCase()}: ${ruler}`}
              >
                {getAlertLabels.severity(alert.severity)}: {ruler}
              </Ruler>
            )}
          </ParameterCell>

          {/*
            `null` até a Fase 3 existir. A coluna fica visível e vazia de propósito:
            escondê-la faria a tabela mudar de forma quando o score chegar.
          */}
          <span>{alert.score ?? '—'}</span>

          <span>
            {format(parseISO(alert.occurredAt), "dd/MM/yyyy 'às' HH:mm")}
          </span>

          {/*
            Vazio na fila, preenchido no histórico resolvido. A coluna existe nos
            dois para a tabela não mudar de forma quando o filtro "Situação" troca.
          */}
          <Outcome title={getAppointmentOutcomeLabel(alert.closeReason)}>
            {getAppointmentOutcomeLabel(alert.closeReason)}
          </Outcome>

          <Actions>
            <WhatsappAction alert={alert} />

            {/*
              "Tratar" só na fila. Fora dela o botão vira o rótulo do estado em vez
              de sumir: a linha continua explicando por que não há ação — está com
              alguém, já fechou, ou foi gravada sem fila. Sem isso, em "Todos" um
              alerta registrado era indistinguível de um da fila (achado 2 do QA).
            */}
            {alert.state === AlertStateEnum.IN_QUEUE &&
              (canTreat ? (
                <TreatButton type="button" onClick={() => onTreat(alert)}>
                  Tratar
                </TreatButton>
              ) : (
                <TreatButton
                  as="span"
                  aria-disabled
                  title={CANNOT_TREAT_MESSAGE}
                >
                  Tratar
                </TreatButton>
              ))}

            {alert.state !== AlertStateEnum.IN_QUEUE && (
              <TreatButton
                as="span"
                aria-disabled
                title={
                  alert.state === AlertStateEnum.IN_SERVICE
                    ? 'Já está em atendimento'
                    : getAlertLabels.state(alert.state)
                }
              >
                {getAlertLabels.state(alert.state)}
              </TreatButton>
            )}

            <ViewButton type="button" onClick={() => onView(alert)}>
              Visualizar
            </ViewButton>
          </Actions>
        </BodyRow>
      );
    })}
  </Container>
);

export default AlertsTable;
